import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ListHeading, ListSubheading, Tree, Flex, Badge, Icon } from '@oliasoft-open-source/react-ui-library';
import { convertToKebabCase } from 'client/common/utils/string-utils';
import translations from 'src/internationalisation/translation-map.json';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';

const ActivitiesList = ({
  entity,
  templates,
  isLoading,
  activeOperation,
  addInitialActivity,
  showDeleteActivitiesModalUpdated,
  reorderActivities,
  showActivitiesForm,
  rigOperationTemplates,
  handleTaskImportModal,
  setIsLinkedTaskImport,
  setActivityParentId,
}) => {
  const { t } = useTranslation();
  const { company: companyId } = useParams();

  const [expanded, setExpanded] = useState(true);
  const treeRef = useRef(null);
  const expandAllItems = () => treeRef.current.openAll();
  const collapseAllItems = () => treeRef.current.closeAll();
  const openNode = (nodeId) => treeRef.current.open(nodeId);
  const onClickExpandCollapse = () => {
    if (expanded) {
      collapseAllItems();
    } else {
      expandAllItems();
    }
    setExpanded(!expanded);
  };
  const getRigOperationTemplateName = (activity) => {
    const rigTemplate = rigOperationTemplates.find((template) =>
      template.activities?.some(
        (templateActivity) => templateActivity.rigOperationTemplateActivityId === activity.rigTemplateActivityId,
      ),
    );

    return rigTemplate ? rigTemplate.name : '';
  };

  const activitiesList = activeOperation?.activities
    ? activeOperation.activities
        .filter((a) => !a?.isBranch)
        .map((activity) => ({
          id: activity.id,
          testId: `activities-list-${convertToKebabCase(activity.name)}-item`,
          name: activity.parentId ? (
            <Flex gap="var(--padding-xs)" alignItems="center" wrap={false}>
              <Badge title="RISK" small />
              {activity.name}
              {activity.withBranch ? <Icon icon="fork" /> : null}
            </Flex>
          ) : (
            activity.name
          ),
          droppable: true,
          active: activity.active,
          parent: activity.parentId || 0,
          onClick: () => showActivitiesForm({ activityId: activity.id, entity }),
          actions: [
            ...((!!activity?.rigTemplateActivityId || !!activity?.isImported) && !activity?.isLinked
              ? [
                  {
                    icon: 'download',
                  },
                ]
              : []),
            ...(!!activity?.rigTemplateActivityId && activity?.isLinked
              ? [
                  {
                    icon: 'link',
                    tooltip: `Rig Operation Template: ${getRigOperationTemplateName(activity)}`,
                  },
                ]
              : []),
            {
              label: t(translations.more),
              testId: `activities-list-${convertToKebabCase(activity.name)}-more-button`,
              subActions: [
                {
                  label: t(translations.delete),
                  icon: 'delete',
                  testId: `activities-list-${convertToKebabCase(activity.name)}-delete-button`,
                  onClick: () =>
                    showDeleteActivitiesModalUpdated({
                      selectedId: activity.id,
                      visible: true,
                    }),
                },
                ...(activity?.withBranch
                  ? []
                  : [
                      {
                        label: t(translations.activityModel_addRisk),
                        icon: 'add',
                        testId: `activities-list-${convertToKebabCase(activity.name)}-add-risk-button`,
                        onClick: () => {
                          addInitialActivity(
                            activity.name,
                            templates,
                            entity,
                            companyId,
                            activity.id,
                            activity.sectionsOperationActivityId,
                          );
                          openNode(activity.id);
                        },
                      },
                      {
                        label: t(translations.activityModel_importFromTemplate),
                        testId: `activities-list-${convertToKebabCase(activity.name)}-import-from-template-button`,
                        onClick: () => {
                          setIsLinkedTaskImport(false);
                          handleTaskImportModal(true);
                          setActivityParentId(activity.id);
                        },
                        icon: 'download',
                      },
                      {
                        label: t(translations.activityModel_importAndLinkToTemplate),
                        testId: `activities-list-${convertToKebabCase(
                          activity.name,
                        )}-import-and-link-to-template-button`,
                        onClick: () => {
                          setIsLinkedTaskImport(true);
                          handleTaskImportModal(true);
                          setActivityParentId(activity.id);
                        },
                        icon: 'link',
                      },
                    ]),
              ].filter(Boolean),
            },
          ],
        }))
    : [];

  return (
    <>
      <ListHeading
        testId="activities-list-heading"
        name={activeOperation?.name || t(translations.activityModel_newOperation)}
      />
      <ListSubheading
        testId="activities-list-subheading"
        item={{
          name: `${t(translations.activities)} (${activitiesList.length})`,
          actions: [
            {
              label: 'Expand/collapse',
              icon: expanded ? 'collapse all' : 'expand all',
              testId: 'activities-list-subheading-expand-collapse-button',
              onClick: onClickExpandCollapse,
              disabled: isLoading,
            },
            {
              label: 'Add',
              icon: 'add',
              testId: 'activities-list-subheading-more-button',
              subActions: [
                {
                  label: t(translations.add),
                  testId: 'activities-list-subheading-add-button',
                  onClick: () =>
                    addInitialActivity(
                      t(translations.activityModel_newActivity),
                      templates,
                      entity,
                      companyId,
                      0,
                      null,
                    ),
                  icon: 'add',
                },
                {
                  label: t(translations.activityModel_importFromTemplate),
                  testId: 'activities-list-subheading-import-from-template-button',
                  onClick: () => {
                    setIsLinkedTaskImport(false);
                    handleTaskImportModal(true);
                    setActivityParentId(null);
                  },
                  icon: 'download',
                },
                {
                  label: t(translations.activityModel_importAndLinkToTemplate),
                  testId: 'activities-list-subheading-import-and-link-to-template-button',
                  onClick: () => {
                    setIsLinkedTaskImport(true);
                    handleTaskImportModal(true);
                    setActivityParentId(null);
                  },
                  icon: 'link',
                },
              ],
              disabled: isLoading,
            },
          ],
        }}
      />
      <Tree
        testId="activities-list"
        list={{
          items: activitiesList,
        }}
        draggable
        noHeader
        onListReorder={(reordered) => reorderActivities(reordered, templates, entity, companyId)}
        isInitialOpen={true}
        treeRef={treeRef}
      />
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const {
    companySettings: { rigOperationTemplates },
  } = entities;

  return {
    rigOperationTemplates,
  };
};

const Container = withErrorBoundary(connect(mapStateToProps)(ActivitiesList));

export { Container as ActivitiesList };
